import { Box, Card, Input, InputAdornment, Menu, CircularProgress, Link, Button, Select, Typography, Stack, MenuItem, IconButton, Container, Chip, Tooltip, Alert, Dialog } from "@mui/material";
import ReactDOM from 'react-dom/client';
import L from "leaflet";
import 'leaflet-providers';
// import 'leaflet-draw';
// import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useAuthContext } from "src/auth/hooks";
import Iconify from "src/components/iconify";
import SimpleDialog from "src/components/simple-dialog";
import TruxSimpleTextField from "src/components/inputs/trux-simple-text-field";
import NoneFoundReport from "src/components/none-found-report";
import useModelData, { replaceById } from "src/hooks/use-model-data";
import { Device, DeviceHistoryLineWithCityName, DeviceWithMapInfo, EldEngineImmobilizer, EldLandmark, Shape } from "src/models";
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { formatDateForPreview, formatDatetimeForPreview, formatTimeForPreview, today } from "src/utils/date-utils";
import { useSettingsContext } from "src/components/settings";

import { Colors } from "src/theme/palette";
import TruxSimpleSelectField from "src/components/inputs/trux-simple-select-field";
import { Constants } from "src/constants";

import TruxFormField from "src/components/inputs/trux-form-field";
import { enqueueSnackbar } from "notistack";
import { MapTypeSelector, useMapTypeSelector } from "src/pages/app/eld/eld-landmarks/eld-landmark-map-modal";

import { useVirtualizer } from '@tanstack/react-virtual';
import type { VirtualizerOptions } from '@tanstack/react-virtual'
import TruxSimpleDateField from "src/components/inputs/trux-simple-date-field";
import { usePrevious } from "src/hooks/use-previous";
import dayjs from "dayjs";

import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent, {timelineOppositeContentClasses} from '@mui/lab/TimelineOppositeContent';

import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import TruxDateTimeField from "src/components/inputs/trux-date-time-field";
import TruxSimpleDateTimeField from "src/components/inputs/trux-simple-date-time-field";
import { VideoJS } from "./video-js";

export const speedPerState = {"AL": 70,"AK": 65,"AZ": 75,"AR": 65,"CA": 55,"CO": 75,"CT": 65,"DE": 65,"DC": 55,"FL": 70,"GA": 70,"HI": 60,"ID": 70,"IL": 70,"IN": 65,"IA": 70,"KS": 75,"KY": 65,"LA": 75,"ME": 75,"MD": 70,"MA": 65,"MI": 65,"MN": 70,"MS": 70,"MO": 70,"MT": 65,"NE": 75,"NV": 80,"NH": 65,"NJ": 65,"NM": 75,"NY": 65,"NC": 70,"ND": 75,"OH": 70,"OK": 75,"OR": 55,"PA": 70,"RI": 65,"SC": 70,"SD": 80,"TN": 70,"TX": 75,"UT": 75,"VT": 65,"VA": 70,"WA": 60,"WV": 70,"WI": 70,"WY": 75};

type PageModel = {
  devices?: DeviceWithMapInfo[],
  eldEngineImmobilizers?: EldEngineImmobilizer[],
  eldLandmarks?: EldLandmark[],
  eldConnectedVehicleUnitNumbers: string[],
}

type MapData = { map: L.Map, markersData: { marker: L.Marker, deviceUuid: string }[] };

type DeviceHistoryLineWithCityNameGroup = DeviceHistoryLineWithCityName & {
  lines: DeviceHistoryLineWithCityName[]
}

export default function Page() {
  const dataLoader = useCallback(() => ({
    devices: axios.get(endpoints.devices.findAll).then(response => response.data),
    eldEngineImmobilizers: axios.get(endpoints.eldEngineImmobilizer.findAll).then(response => response.data),
    eldLandmarks: axios.get(endpoints.eldLandmarks.findAll).then(response => response.data),
    eldConnectedVehicleUnitNumbers: [] // axios.get(endpoints.eldConnectedVehicleUnitNumbers.findAll).then(response => response.data),
  }), []);
  const {
    data,
    updateData,
    setData,
    error,
    loading,
    setShouldRefresh
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      console.log(error);
      alert(error);
    }
  }, [loading, error]);


  const processDevicesUpdateResponse = useCallback((response: any) => {
    let changeFound = false;
    const devices = (data?.devices || []);
    devices.forEach(device => {
      (response.data as DeviceWithMapInfo[]).forEach((resDevice) => {
        if (device.deviceUuid === resDevice.deviceUuid) {
          // if there are updates we should patch in memory
          if (device.lastPing !== resDevice.lastPing
            || device.velocity !== resDevice.velocity
            || device.direction !== resDevice.direction
            || device.battery !== resDevice.battery
            || JSON.stringify(device.eldDrivers) !== JSON.stringify(resDevice.eldDrivers)
            || device.lastPosLat !== resDevice.lastPosLat
            || device.lastPosLong !== resDevice.lastPosLong) {

            device.lastPing = resDevice.lastPing;
            device.lastPosLat = resDevice.lastPosLat;
            device.lastPosLong = resDevice.lastPosLong;
            device.velocity = resDevice.velocity
            device.direction = resDevice.direction
            device.battery = resDevice.battery
            device.eldDrivers = resDevice.eldDrivers;

            changeFound = true;
          }
        }
      })
    })
    if (changeFound) {
      updateData("devices")([...devices]);
    }
  }, [data?.devices, updateData]);


  useEffect(() => {
    const interval = setInterval(() => {
      axios.get(endpoints.devices.findAll).then(processDevicesUpdateResponse)
    }, 10_000)
    return () => {
      clearInterval(interval);
    }
  }, [processDevicesUpdateResponse])

  const [selectedUuid, setSelectedUuid] = useState("");
  const [mapData, setMapData] = useState<MapData | null>(null);

  const [searchText, setSearchText] = useState("");
  const [selectedType, setSelectedType] = useState("0");

  const [deviceHistory, setDeviceHistory] = useState<DeviceHistoryLineWithCityNameGroup[] | null>(null);
  const [selectedMarker, setSelectedMarker] = useState(-1);

  const eldEngineImmobilizerByDeviceId = useMemo(() => {
    const map = new Map<number, EldEngineImmobilizer>();
    data?.eldEngineImmobilizers?.forEach(eldEngineImmobilizer => map.set(eldEngineImmobilizer.deviceId, eldEngineImmobilizer));
    return map;
  }, [data?.eldEngineImmobilizers]);

  const filteredDevices = useMemo(() => {
    // console.log("REFILTERING");
    if (!data?.devices) {
      return [];
    }
    const allDevices = (data.devices || []).filter(device => device.deviceType.toLowerCase().indexOf(searchText.toLowerCase()) > -1 || device.externalIdentifier.toLowerCase().indexOf(searchText.toLowerCase()) > -1);

    return allDevices.filter(device => {
      if (selectedType === "0") {
        return true;
      }
      if (selectedType === "-1") {
        return device.deviceType.startsWith("ELD Truck");
      }

      return (device.externalIdentifier || "").startsWith(`${selectedType}|`);
    });
  }, [data?.devices, searchText, selectedType])

  const { session } = useAuthContext();
  const timezone = session?.organization?.companyTimezone as string;

  return <Box display="flex" flexDirection="row" height="100%" marginTop="-15px" position="relative">
    {loading && !data && <Box component="div" sx={{ marginTop: "20vh", textAlign: "center", margin: "auto" }}><CircularProgress size={50} /></Box>}

    {!loading && error &&
      <Card sx={{m: 4, width: "100%", p:4, textAlign: "center" }}>
          <Typography component="div" padding={2} color="error">
            <Box display="flex" gap={1} justifyContent="center">
              <Iconify icon="octicon:alert-16" /> <i>Error loading the data {error}</i>
            </Box>
          </Typography>
      </Card>}


    {!loading && data && <Box position="absolute" left="0px" top="0px" right="0px" bottom="-15px" display="flex" flexDirection="row">
      <DeviceSidebar mapData={mapData} updateData={updateData} eldEngineImmobilizerByDeviceId={eldEngineImmobilizerByDeviceId}
        onUpdated={() => {
          if (data.devices) {
            updateData("devices")([...data.devices]);
          }
        }}
        processDevicesUpdateResponse={processDevicesUpdateResponse}
        onEldEngineImmobilizerUpdated={(eldEngineImmobilizer: EldEngineImmobilizer) => {
          if (data.eldEngineImmobilizers) {
            updateData("eldEngineImmobilizers")(replaceById(data!.eldEngineImmobilizers, eldEngineImmobilizer));
          }
        }}
        setSelectedUuid={setSelectedUuid}
        filteredDevices={filteredDevices}
        searchText={searchText}
        setSearchText={setSearchText}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        deviceHistory={deviceHistory || []}
        setDeviceHistory={setDeviceHistory}
        selectedMarker={selectedMarker} />
      <DeviceMap
        data={data}
        filteredDevices={filteredDevices}
        mapData={mapData}
        setMapData={setMapData}
        selectedUuid={selectedUuid}
        deviceHistory={deviceHistory}
        setSelectedMarker={setSelectedMarker}
        timezone={timezone}
        processDevicesUpdateResponse={processDevicesUpdateResponse}
      />
    </Box>}
  </Box>
}
function isTheSame(lastLat: string, lastLong: string, lat: string, long: string) {
  const lastLatF = Number.parseFloat(lastLat);
  if (Number.isNaN(lastLatF)) {
    return false;
  }
  const lastLongF = Number.parseFloat(lastLong);
  if (Number.isNaN(lastLongF)) {
    return false;
  }
  const latF = Number.parseFloat(lat);
  if (Number.isNaN(latF)) {
    return false;
  }
  const longF = Number.parseFloat(long);
  if (Number.isNaN(longF)) {
    return false;
  }

  const latDiff = Math.abs(lastLatF - latF);
  const lngDiff = Math.abs(lastLongF - longF);
  return latDiff < 0.00035 && lngDiff < 0.00035
}


function DeviceSidebar({ filteredDevices, processDevicesUpdateResponse, onEldEngineImmobilizerUpdated, eldEngineImmobilizerByDeviceId, onUpdated, updateData, setSelectedUuid, mapData, searchText, setSearchText, selectedType, setSelectedType, deviceHistory, setDeviceHistory, selectedMarker }: {
  filteredDevices: DeviceWithMapInfo[],
  updateData: any,
  processDevicesUpdateResponse: (a: any) => void,
  onEldEngineImmobilizerUpdated: (a: EldEngineImmobilizer) => void,
  eldEngineImmobilizerByDeviceId: Map<number, EldEngineImmobilizer>,
  searchText: string,
  setSearchText: (a: string) => void,
  selectedType: string,
  setSelectedType: (a: string) => void,
  mapData: MapData | null, onUpdated: VoidFunction, setSelectedUuid: (a: string) => void
  deviceHistory: DeviceHistoryLineWithCityNameGroup[],
  setDeviceHistory: (history: DeviceHistoryLineWithCityNameGroup[] | null) => void;
  selectedMarker: number,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const { session } = useAuthContext();
  const [selectedDevice, setSelectedDevice] = useState<DeviceWithMapInfo>();
  const [loading, setLoading] = useState(false);
  const [newDeviceType, setNewDeviceType] = useState("");

  const timezone = session?.organization?.companyTimezone as string;

  const historyRef = useRef<HTMLDivElement>(null);

  const [deviceForHistory, setDeviceForHistory] = useState<DeviceWithMapInfo>();
  const [dateForHistory, setDateForHistory] = useState(dayjs().tz(timezone || 'America/Chicago').format("YYYY-MM-DD"));
  const [historicLoading, setHistoricLoading] = useState(false);

  const deviceForHistoryPrev = usePrevious(deviceForHistory);
  const dateForHistoryPrev = usePrevious(dateForHistory);

  const historyVirtualizer = useVirtualizer({
    count: deviceHistory?.length || 0,
    getScrollElement: () => historyRef.current,
    estimateSize: () => 70,
    overscan: 5,
  });

  const historyVirtualItems = historyVirtualizer.getVirtualItems();

  useEffect(() => {
    if (selectedMarker !== -1) {
      historyVirtualizer.scrollToIndex(selectedMarker, {align: 'center', behavior: "smooth"});
    }
  }, [historyVirtualizer, selectedMarker])


  useEffect(() => {
    if (deviceForHistory && (deviceForHistory !== deviceForHistoryPrev || dateForHistory !== dateForHistoryPrev)) {
      setHistoricLoading(true);
      // load historic data and set loadingHistoric to false
      // axios
      axios.get(`${formatEndpoint(
        endpoints.devices.findAllHistory,
        {
          eldDriverId: 0,
          date: dateForHistory
        })}?deviceId=${deviceForHistory.id}`).then((response) => {
        // handle retrieved history lines
        if (response.data) {

          const deviceHistories: DeviceHistoryLineWithCityNameGroup[] = [];

          response.data.lines.forEach((line: any) => {
            const closeIndex = deviceHistories.findIndex(
              (existing) => isTheSame(existing.posLat, existing.posLong, line.posLat, line.posLong)
            );
            if (closeIndex >= 0) {
              deviceHistories[closeIndex].lines.push(line);
            } else {
              deviceHistories.push({
                ...line,
                lines: [],
              });
            }
          });
          setDeviceHistory(deviceHistories);
        }
        setHistoricLoading(false);
      }).catch(() => {
        setHistoricLoading(false);
        enqueueSnackbar("Unexpected exception has occured. Please contact customer support.", {variant: "error"})
      })
    }
  }, [deviceForHistory, dateForHistory, deviceForHistoryPrev, dateForHistoryPrev, setDeviceHistory])

  const videoRequestState = useVideoRequestState();

  return <Box sx={{position: "relative"}}>
    <Box sx={{ background: "#ffffff", boxShadow: "5px 0 7px -7px rgba(0, 0, 0, 0.5)", zIndex: 0, overflowY: "auto", overscrollBehavior: "none", height: "100%", width: (collapsed ? "0px" : {xs: "240px", md: "320px"})}} display="flex" flexDirection="column">
      {deviceForHistory ? <Box p={1} display="flex" flexGrow={1} flexDirection="column" gap={2}>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: "center" }}>
            <IconButton
              onClick={() => {
                setDeviceForHistory(undefined);
                setDeviceHistory(null);
              }}
            >
              <Iconify icon="solar:arrow-left-linear" />
            </IconButton>
            <Box sx={{pl: 1}}>{deviceForHistory.deviceType}</Box>
          </Box>
          <Box width="100%" display="flex" flexDirection="column" alignItems="center">
            <TruxSimpleDateField
              value={dateForHistory}
              onChange={(d) => setDateForHistory(d!)}
              limitWidth={false}
            />
          </Box>

          {historicLoading ? (
            <Box sx={{display: "flex", justifyContent: 'center'}}>
              <CircularProgress/>
            </Box>
          ) : (

            <Timeline sx={ {
              display: "flex", flexDirection: "column",
              padding: "0",
              [`& .${timelineOppositeContentClasses.root}`]: {flex: 0.2},
            }}>
              {!deviceHistory || deviceHistory.length === 0 && <Box pl={1}>
                <Typography component="div" padding={2} color="grey">
                  <Box display="flex" gap={1}>
                    <Iconify icon="octicon:alert-16" /> No History Recorded
                  </Box>
                </Typography>
              </Box>}

              <div
                ref={historyRef}
                style={{
                  height: '100%',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  overscrollBehavior: 'none',
                  contain: 'strict',
                }}
              >
                <div
                  style={{
                    height: historyVirtualizer.getTotalSize(),
                    width: '100%',
                    position: 'relative',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${historyVirtualItems[0]?.start ?? 0}px)`,
                    }}
                  >
                    {historyVirtualItems?.map((virtualRow, i) => {
                      const deviceHistoryLine = deviceHistory[virtualRow.index];

                      return <TimelineItem
                        key={i}
                        data-index={virtualRow.index}
                        ref={historyVirtualizer.measureElement}
                        sx={{
                          // display: "flex",
                          // alignItems: "center",
                          '&:hover': {
                              scale: '1.03',
                              '& .MuiTimelineDot-root': { backgroundColor: '#31bb3f' },
                              cursor: "pointer",
                            },
                            ...(selectedMarker === virtualRow.index && { '& .MuiTimelineDot-root': {backgroundColor: '#31bb3f'}})
                          }}
                          onClick={() => {
                            if (!deviceHistoryLine.posLat || !deviceHistoryLine.posLong) {
                              return;
                            }
                            mapData?.map?.setView([parseFloat(deviceHistoryLine.posLat), parseFloat(deviceHistoryLine.posLong)], 12, {
                              animate: true,
                              duration: 1.25,
                              easeLinearity: 0.25
                            });
                          }} >
                          {/* onClick={zoom to location of item} */}
                        <TimelineOppositeContent color="textSecondary">
                          <Box>{formatTimeForPreview(deviceHistoryLine.recordedAt, timezone ?? 'America/Chicago')}</Box>
                          {deviceHistoryLine.lines.length > 0 &&
                            <Box>{formatTimeForPreview(deviceHistoryLine.lines[deviceHistoryLine.lines.length - 1].recordedAt, timezone ?? 'America/Chicago')}</Box>
                          }
                        </TimelineOppositeContent>
                        <TimelineSeparator>
                          <TimelineDot />
                          {i < (deviceHistory.length - 1) && <TimelineConnector />}
                        </TimelineSeparator>
                        <TimelineContent sx={{paddingRight:"0"}}>
                          {deviceHistoryLine.cityName ?? 'N/A'}
                        </TimelineContent>

                      </TimelineItem>
                    })}
                  </div>
                </div>
              </div>
            </Timeline>
          )}
        </Box> : <>
        <Box p={1} display="flex" gap={1} sx={{borderBottom: "1px solid #EEE"}}>
          <Box flex={2}>
            <TruxSimpleTextField value={searchText} onChange={setSearchText} label="Search Devices" />
          </Box>
          <Box flex={1}>
            <TruxSimpleSelectField value={selectedType} onChange={val => setSelectedType(val)}>
              <MenuItem value="0" selected={selectedType === "0"}>All</MenuItem>
              <MenuItem value="-1" selected={selectedType === "-1"}>Trucks</MenuItem>
              <MenuItem value="1" selected={selectedType === "1"}>CT Trailers</MenuItem>
              <MenuItem value="2" selected={selectedType === "2"}>Samsara</MenuItem>
              <MenuItem value="3" selected={selectedType === "3"}>SkyBitz</MenuItem>
              <MenuItem value="4" selected={selectedType === "4"}>Premier</MenuItem>
              <MenuItem value="5" selected={selectedType === "5"}>CT Imobilizer</MenuItem>
            </TruxSimpleSelectField>
          </Box>
        </Box>
        <Box flexGrow={1} sx={{display: "flex", flexDirection: "column"}}>
          {filteredDevices.length === 0 && <Box pl={1}>
            <Typography component="div" padding={2} color="grey">
              <Box display="flex" gap={1}>
                <Iconify icon="octicon:alert-16" /> No Devices Found
              </Box>
            </Typography>
          </Box>}
          <Box sx={{position: "relative", flex: 1}}>
            <Box sx={{position: "absolute", left: "0px", right: "0px", top: "0px", bottom: "0px"}}>
              <VirtualizedDeviceList
                processDevicesUpdateResponse={processDevicesUpdateResponse}
                onEldEngineImmobilizerUpdated={onEldEngineImmobilizerUpdated}
                updateData={updateData}
                eldEngineImmobilizerByDeviceId={eldEngineImmobilizerByDeviceId}
                filteredDevices={filteredDevices}
                mapData={mapData}
                setSelectedDevice={setSelectedDevice}
                setNewDeviceType={setNewDeviceType}
                setSelectedUuid={setSelectedUuid}
                timezone={timezone}
                setDeviceForHistory={setDeviceForHistory}

                setLiveVideoOpen={(liveVideoOpen: boolean, device: DeviceWithMapInfo) => {
                  videoRequestState.setState({...videoRequestState.state, device, liveVideoOpen, hls: undefined})

                  const liveVideoRequest = axios.post(endpoints.cameras.liveVideo, JSON.stringify({deviceId: device.id}));
                  videoRequestState.request.current = liveVideoRequest;

                  liveVideoRequest.then(response => {
                    if (videoRequestState.request.current === liveVideoRequest) {
                      if (response.data?.hls) {
                        setTimeout(() => {
                          videoRequestState.setState(s => ({...s, hls: response.data?.hls}));
                        }, 2000);
                      } else {
                        enqueueSnackbar("Live Video could not be started. Please try later.", {variant: "error"});
                        videoRequestState.setState(s => ({...s, liveVideoOpen: false}))
                      }
                    }
                  }).catch(() => {
                    enqueueSnackbar("An unexpected error has occured. Please contact customer support.", {variant: "error"});
                    videoRequestState.setState(s => ({...s, liveVideoOpen: false}))
                  });
                }}


                setLivePhotoOpen={(livePhotoOpen: boolean, device: DeviceWithMapInfo) => {
                  videoRequestState.setState({...videoRequestState.state, device, livePhotoOpen, image: undefined})

                  const livePhotoRequest = axios.post(endpoints.cameras.livePhoto, JSON.stringify({deviceId: device.id}));
                  videoRequestState.request.current = livePhotoRequest;

                  livePhotoRequest.then(response => {
                    if (videoRequestState.request.current === livePhotoRequest) {
                      if (response.data?.image) {
                        videoRequestState.setState(s => ({...s, image: response.data?.image}));
                      } else {
                        enqueueSnackbar("Image could not be loaded. Please try later.", {variant: "error"});
                        videoRequestState.setState(s => ({...s, livePhotoOpen: false}))
                      }
                    }
                  }).catch(() => {
                    enqueueSnackbar("An unexpected error has occured. Please contact customer support.", {variant: "error"});
                    videoRequestState.setState(s => ({...s, livePhotoOpen: false}))
                  });
                }}
                setRequestHistoricVideoOpen={(requestHistoricVideoOpen: boolean, device: DeviceWithMapInfo) => videoRequestState.setState({...videoRequestState.state, device, requestHistoricVideoOpen})}
              />
            </Box>
          </Box>
        </Box>
      </>}

      {selectedDevice && <SimpleDialog
        title="Update Asset"
        open={!!selectedDevice}
        onCancel={() => {
          setSelectedDevice(undefined);
        }}
        onSubmit={() => {
          setLoading(true);

          axios.post(endpoints.devices.updateDeviceType, {
            deviceId: selectedDevice.id,
            deviceType: newDeviceType,
          }).then(() => {
            enqueueSnackbar("Successfully updated the device!");
            selectedDevice.deviceType = newDeviceType;
            onUpdated();
          }).finally(() => {
            setSelectedDevice(undefined);
            setLoading(false);
          })
        }}
        submitDisabled={loading}
        submitButton="Update Asset"
        submitButtonIcon={<Iconify icon="basil:save-solid" />}
        submitButtonColor="info">

        <Stack direction="column" gap={1}>
          <TruxFormField label="Device Label">
            <TruxSimpleTextField value={newDeviceType} onChange={(val) => setNewDeviceType(val)} />
          </TruxFormField>
        </Stack>
      </SimpleDialog >}
    </Box>

    <Box sx={{
      position: "absolute",
      top: "50%",
      right: "-25px",
      zIndex: 1000,
      padding: "2px",
      paddingTop: "20px",
      paddingBottom: "20px",
      borderTopRightRadius: "4px",
      borderBottomRightRadius: "4px",
      background: "#fafafa",
      border: "1px solid #e5e6e6",
      borderLeft: "none",
    }} onClick={() => {
      setCollapsed(!collapsed);
      setTimeout(() => {
        mapData?.map?.invalidateSize();
      }, 500);
    }}>
      {collapsed ? <Iconify icon="line-md:chevron-right" /> : <Iconify icon="line-md:chevron-left" />}
    </Box>

    {videoRequestState.state.livePhotoOpen
      && videoRequestState.state.device
      && <LivePhoto videoRequestState={videoRequestState} />
    }
    {videoRequestState.state.liveVideoOpen
      && videoRequestState.state.device
      && <LiveVideo videoRequestState={videoRequestState} />
    }
    {videoRequestState.state.requestHistoricVideoOpen
      && videoRequestState.state.device
      && <RequestHistoricVideo videoRequestState={videoRequestState} />
    }
  </Box>
}
type VideoRequestStateState = {
  device?: DeviceWithMapInfo,
  liveVideoOpen: boolean,
  livePhotoOpen: boolean,
  requestHistoricVideoOpen: boolean,
  image?: string,
  hls?: string,
}
type VideoRequestState = {
  state: VideoRequestStateState,
  setState: (a: VideoRequestStateState) => void,
  request?: any
}
function useVideoRequestState() {
  const [state, setState] = useState<VideoRequestStateState>({
    device: undefined as DeviceWithMapInfo | undefined,
    liveVideoOpen: false,
    livePhotoOpen: false,
    requestHistoricVideoOpen: false,
    image: undefined as string | undefined,
  })
  const request = useRef(undefined as any);

  return {
    state, setState, request
  }
}




type VirtualizedDeviceListProps = {
  processDevicesUpdateResponse: (a: any) => void,
  onEldEngineImmobilizerUpdated: (a: EldEngineImmobilizer) => void,
  updateData: any,
  eldEngineImmobilizerByDeviceId: Map<number, EldEngineImmobilizer>,
  filteredDevices: DeviceWithMapInfo[],
  mapData: MapData | null,
  setSelectedDevice: React.Dispatch<React.SetStateAction<DeviceWithMapInfo | undefined>>,
  setNewDeviceType: (a: string) => void,
  setSelectedUuid: (a: string) => void,
  timezone: string,
  setDeviceForHistory: (device: DeviceWithMapInfo) => void,
  setLiveVideoOpen: (a: boolean, device: DeviceWithMapInfo) => void,
  setLivePhotoOpen: (a: boolean, device: DeviceWithMapInfo) => void,
  setRequestHistoricVideoOpen: (a: boolean, device: DeviceWithMapInfo) => void,
}

function VirtualizedDeviceList({
  processDevicesUpdateResponse,
  onEldEngineImmobilizerUpdated,
  updateData,
  eldEngineImmobilizerByDeviceId,
  filteredDevices,
  mapData,
  setSelectedDevice,
  setNewDeviceType,
  setSelectedUuid,
  timezone,
  setDeviceForHistory,
  setLiveVideoOpen,
  setLivePhotoOpen,
  setRequestHistoricVideoOpen,
}: VirtualizedDeviceListProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: filteredDevices.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 35,
    overscan: 5,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  return <SimpleBar
    scrollableNodeProps={{ ref: parentRef }} // This passes your ref to the scrollable element
    style={{
    height: '100%',
    overflowX: 'hidden',
    overscrollBehavior: 'none',
  }}>
    <div
      style={{
        height: rowVirtualizer.getTotalSize(),
        width: '100%',
        position: 'relative',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
        }}
      >
        {virtualItems.map((virtualRow) => (
          <div
            key={virtualRow.key}
            data-index={virtualRow.index}
            ref={rowVirtualizer.measureElement}
          >
            <DeviceBlock
              processDevicesUpdateResponse={processDevicesUpdateResponse}
              onEldEngineImmobilizerUpdated={onEldEngineImmobilizerUpdated}
              updateData={updateData}
              eldEngineImmobilizer={eldEngineImmobilizerByDeviceId.get(filteredDevices[virtualRow.index].id)}
              device={filteredDevices[virtualRow.index]}
              mapData={mapData}
              setSelectedDevice={setSelectedDevice}
              setNewDeviceType={setNewDeviceType}
              setSelectedUuid={setSelectedUuid}
              timezone={timezone}
              setDeviceForHistory={setDeviceForHistory}
              setLiveVideoOpen={setLiveVideoOpen}
              setLivePhotoOpen={setLivePhotoOpen}
              setRequestHistoricVideoOpen={setRequestHistoricVideoOpen}
            />
          </div>
        ))}
      </div>
    </div>
  </SimpleBar>
}

type DeviceBlockType = {
  updateData: any,
  device: DeviceWithMapInfo,
  processDevicesUpdateResponse: (a: any) => void,
  onEldEngineImmobilizerUpdated: (a: EldEngineImmobilizer) => void,
  mapData: MapData | null,
  setSelectedDevice: (a: DeviceWithMapInfo) => void,
  setNewDeviceType: (a: string) => void,
  setSelectedUuid: (a: string) => void,
  timezone: string,
  setDeviceForHistory: (device: DeviceWithMapInfo) => void,
  eldEngineImmobilizer?: EldEngineImmobilizer,
  setLiveVideoOpen: (a: boolean, device: DeviceWithMapInfo) => void,
  setLivePhotoOpen: (a: boolean, device: DeviceWithMapInfo) => void,
  setRequestHistoricVideoOpen: (a: boolean, device: DeviceWithMapInfo) => void,
};

function DeviceBlock(
  {device, mapData, processDevicesUpdateResponse, onEldEngineImmobilizerUpdated, updateData, eldEngineImmobilizer, setSelectedDevice, setNewDeviceType, setSelectedUuid, setDeviceForHistory, timezone, setLiveVideoOpen, setLivePhotoOpen, setRequestHistoricVideoOpen}: DeviceBlockType) {

  const connected = !!device.eldDrivers?.find(eldDriver => eldDriver.connected);
  return <Box>
    <Box>
      <Card sx={{p: 1, display:"flex", border: "none", borderBottom: "1px solid #EEE", borderRadius:"0px", justifyContent: "space-between", flexDirection:"row", '&:hover': device.lastPing && { cursor: "pointer", background: "#FAFAFA" }, position: "relative"}} onClick={() => {
        if (!device.lastPing) {
          return;
        }
        const zoom = mapData?.map?.getZoom() || 100;

        mapData?.markersData?.forEach(marker => {
          if (marker.deviceUuid === device.deviceUuid) {
            if (zoom < 12) {
              mapData?.map?.setView(marker.marker.getLatLng(), 12, {
                animate: true,
                duration: 25,
              });
            } else {
              mapData?.map?.setView(marker.marker.getLatLng());
            }
            setTimeout(() => {
              marker.marker.openPopup();
            }, 51);
          }
        });
      }}>
        <Box>
          <Box sx={{display: "flex", alignItems: "center"}}>
            <Box sx={{paddingRight: "3px"}}>
              {device.externalIdentifier ? <Link color={Colors.darkGreen} onClick={() => {
                setSelectedDevice(device);
                setNewDeviceType(device.deviceType);
              }}>{device.deviceType}</Link> : device.deviceType.replace("ELD Truck", "Truck")}
            </Box>


            {device.camera && <CameraMenu
              device={device}
              setLiveVideoOpen={setLiveVideoOpen}
              setLivePhotoOpen={setLivePhotoOpen}
              setRequestHistoricVideoOpen={setRequestHistoricVideoOpen}
            />}

            {device.deviceType.startsWith("ELD Truck #") && <Tooltip title={connected ? "ELD Connected" : "ELD Disconnected"}>
              {connected ? <Iconify icon="ph:cell-signal-full" color="#4CAF50" /> : <Iconify icon="ph:cell-signal-slash" color="#ff5631" />}
            </Tooltip>}

            {device.velocity !== undefined && overSpeed(device) && <Chip sx={{height: "20px", fontSize: "11px"}} size="small" color="error" label={`${device.velocity.toFixed(0)}mph`} />}
          </Box>
          {device.lastPing ? <>
            <Box fontSize="12px" color="grey">{device.location}</Box>
            <Box fontSize="12px" color="grey">Last Ping: {formatDatetimeForPreview(device.lastPing, timezone)}</Box>
            {device.externalIdentifier && !device.externalIdentifier.startsWith("2|") && <Box fontSize="12px" color="grey">Battery: {device.battery}%</Box>}
          </> : <Box fontSize="12px" color="grey">Last position unknown</Box>}
        </Box>
        <Box>
          <DeviceMoreButton processDevicesUpdateResponse={processDevicesUpdateResponse} onEldEngineImmobilizerUpdated={onEldEngineImmobilizerUpdated} updateData={updateData} eldEngineImmobilizer={eldEngineImmobilizer} device={device} setDeviceForHistory={setDeviceForHistory} />
        </Box>
      </Card>
    </Box>
  </Box>
}

function CameraMenu({
  device,
  setLiveVideoOpen,
  setLivePhotoOpen,
  setRequestHistoricVideoOpen
}: {
  device: DeviceWithMapInfo,
  setLiveVideoOpen: (a: boolean, device: DeviceWithMapInfo) => void,
  setLivePhotoOpen: (a: boolean, device: DeviceWithMapInfo) => void,
  setRequestHistoricVideoOpen: (a: boolean, device: DeviceWithMapInfo) => void,
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  return <>
    <IconButton onClick={e => {
      setAnchorEl(e.currentTarget);
      e.stopPropagation();
      e.preventDefault();
    }}>
      <Iconify icon="qlementine-icons:camera-16" />
    </IconButton>
    {anchorEl && <Menu
      id="basic-menu"
      anchorEl={anchorEl}
      open={!!anchorEl}
      onClose={() => setAnchorEl(null)}
      MenuListProps={{
        'aria-labelledby': 'basic-button',
      }}
    >

      <MenuItem onClick={(e) => {
        setAnchorEl(null);
        setLiveVideoOpen(true, device);
      }}>Live Video</MenuItem>

      <MenuItem onClick={(e) => {
        setAnchorEl(null);
        setLivePhotoOpen(true, device);
      }}>Live Photo</MenuItem>

      <MenuItem onClick={(e) => {
        setAnchorEl(null);
        setRequestHistoricVideoOpen(true, device);
      }}>Request Historic Video</MenuItem>
    </Menu>}
  </>
}

type RequestDialog = {
  videoRequestState: VideoRequestState;
};

function LivePhoto({videoRequestState}: RequestDialog) {
  const open= videoRequestState.state.livePhotoOpen;
  const setOpen= (livePhotoOpen: boolean) => videoRequestState.setState({...videoRequestState.state, livePhotoOpen});

  return <SimpleDialog
    title="Live Photo"
    open={open}
    onCancel={() => setOpen(false)}
    sx={{".MuiDialogContent-root": {overflowY: "visible"}}}
  >
    {videoRequestState.state.image ? <img src={videoRequestState.state.image} alt="truck" style={{width: "100%"}} onError={(e) => {
        if (!(e.target as any).attempts) {
          (e.target as any).attempts = 0;
        }
        (e.target as any).attempts+=1;

        if ((e.target as any).attempts < 3) {
          setTimeout(() => {
            (e.target as any).src = (e.target as any).src;
          }, 1000 * (e.target as any).attempts);
        }
      }} /> : <>
      <Box sx={{display: "flex", alignItems: "center", justifyContent: "center"}}>
        <CircularProgress />
      </Box>
      <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", pt: 2}}>
        Taking the photo...
      </Box>
      <Box sx={{display: "flex", alignItems: "center", justifyContent: "center"}}>
        <i>Please be patient. This can take up to one minute.</i>
      </Box>
    </>}
  </SimpleDialog>
}

function LiveVideo({videoRequestState}: RequestDialog) {
  const open= videoRequestState.state.liveVideoOpen;
  const setOpen= (liveVideoOpen: boolean) => videoRequestState.setState({...videoRequestState.state, liveVideoOpen});

  const videoOptions = useMemo(() => ({
    controls: false,
    autoplay: true,
    muted: true, // Often required for autoplay
    fluid: true,
    sources: [{
      src: videoRequestState.state.hls,
      type: 'application/x-mpegURL'
    }]
  }), [videoRequestState.state.hls])

  return <SimpleDialog
    title="Live Video"
    open={open}
    onCancel={() => setOpen(false)}
    sx={{".MuiDialogContent-root": {overflowY: "visible"}}}
  >
    {videoRequestState.state.hls ? <VideoJS options={videoOptions} /> : <>
      <Box sx={{display: "flex", alignItems: "center", justifyContent: "center"}}>
        <CircularProgress />
      </Box>
      <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", pt: 2}}>
        Connecting to the Camera..
      </Box>
      <Box sx={{display: "flex", alignItems: "center", justifyContent: "center"}}>
        <i>Please be patient. This can take up to one minute.</i>
      </Box>
    </>}
  </SimpleDialog>
}
function RequestHistoricVideo({videoRequestState}: RequestDialog) {
  const open= videoRequestState.state.requestHistoricVideoOpen;
  const setOpen= (requestHistoricVideoOpen: boolean) => videoRequestState.setState({...videoRequestState.state, requestHistoricVideoOpen});
  const {device} = videoRequestState.state;

  const now = dayjs();

  const { session } = useAuthContext();
  const timezone = session?.organization?.companyTimezone as string;

  const [loading, setLoading] = useState(false);
  const [fromTime, setFromTime] = useState(now.subtract(60, "seconds").format());
  const [toTime, setToTime] = useState(now.format());
  const [camera, setCamera] = useState(0);

  return <SimpleDialog
    open={open}
    onCancel={() => setOpen(false)}
    title={`Request Video for ${device?.deviceType}`}
    onSubmit={() => {
      setLoading(true);

      axios.post(endpoints.cameras.requestHistoricVideo, JSON.stringify({deviceId: device!.id, fromTime, toTime})).then(response => {
        enqueueSnackbar("Historic video has been requested. If the video is available you will be able to access it in the Video notifications page.", {autoHideDuration: 5000});
      }).catch(() => {
        enqueueSnackbar("An unexpected error has occured.", {variant: "error"});
      }).finally(() => {
        setLoading(false);
      })
    }}
    submitDisabled={loading}
    submitButton="Request Historic Video"
    submitButtonIcon={<Iconify icon="qlementine-icons:camera-16" />}
    submitButtonColor="info"
    sx={{".MuiDialogContent-root": {overflowY: "visible"}}}
  >
    <Stack direction="column" gap={1}>
      <Alert icon={<Box sx={{display:"flex", alignItems: "center", justifyContent: "center"}}><Iconify icon="qlementine-icons:camera-16" /></Box>} severity="success" sx={{mb: 2}}>
        After you request a video you will be able to view it on the Camera Videos page.
      </Alert>

      <TruxFormField label="Camera">
        <TruxSimpleSelectField value={camera} onChange={setCamera} disabled>
          <MenuItem value={0}>Front Camera</MenuItem>
          <MenuItem value={1}>Back Camera (Driver)</MenuItem>
        </TruxSimpleSelectField>
      </TruxFormField>
      <TruxFormField label="From Time">
        <TruxSimpleDateTimeField limitWidth={false} value={fromTime} onChange={setFromTime} timezone={timezone} />
      </TruxFormField>
      <TruxFormField label="To Time">
        <TruxSimpleDateTimeField limitWidth={false} value={toTime} onChange={setToTime} timezone={timezone} />
      </TruxFormField>
    </Stack>
  </SimpleDialog>
}


function DeviceMoreButton({
  updateData,
  processDevicesUpdateResponse,
  onEldEngineImmobilizerUpdated,
  eldEngineImmobilizer,
  device,
  setDeviceForHistory
}: {
  processDevicesUpdateResponse: (a: any) => void,
  updateData: any,
  onEldEngineImmobilizerUpdated: (a: EldEngineImmobilizer) => void,
  eldEngineImmobilizer?: EldEngineImmobilizer,
  device: DeviceWithMapInfo,
  setDeviceForHistory: (device: DeviceWithMapInfo) => void,
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shareFor, setShareFor] = useState("1");
  const [shareLink, setShareLink] = useState();

  const showSpinner = useMemo(() => {
    if (device.externalIdentifier.startsWith("5|")) {
      if (!eldEngineImmobilizer) {
        return false;
      }
      if (eldEngineImmobilizer.whereRequestedAt) {
        if (!eldEngineImmobilizer.whereLoadedAt) {
          // if this was requested more than 10min ago fuck it
          if (dayjs(eldEngineImmobilizer.whereRequestedAt).isAfter(dayjs().add(-10, 'minutes'))) {
            return true;
          }

          return false;
        }

        if (dayjs(eldEngineImmobilizer.whereLoadedAt).isAfter(dayjs(eldEngineImmobilizer.whereRequestedAt)) || dayjs(eldEngineImmobilizer.statusLoadedAt).isAfter(dayjs(eldEngineImmobilizer.whereRequestedAt))) {
          return false;
        }

        if (dayjs(eldEngineImmobilizer.whereRequestedAt).isAfter(dayjs().add(-3, 'minutes'))) {
          if (dayjs(eldEngineImmobilizer.whereLoadedAt).isBefore(dayjs(eldEngineImmobilizer.whereRequestedAt)) || dayjs(eldEngineImmobilizer.statusLoadedAt).isBefore(dayjs(eldEngineImmobilizer.whereRequestedAt))) {
            return true;
          }
        }
      }
    }
    return false;
  }, [device, eldEngineImmobilizer]);

  return <>
    <IconButton disabled={loading || showSpinner} onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();

      if (device.externalIdentifier.startsWith("5|")) {
        if (!eldEngineImmobilizer) {
          return;
        }
        if (eldEngineImmobilizer.whereRequestedAt) {
          if (!eldEngineImmobilizer.whereLoadedAt) {
            return;
          }

          if (dayjs(eldEngineImmobilizer.whereRequestedAt).isAfter(dayjs().add(-3, 'minutes')) && dayjs(eldEngineImmobilizer.whereLoadedAt).isBefore(dayjs(eldEngineImmobilizer.whereRequestedAt))) {
            return;
          }
        }
      }
      handleClick(e);
    }}>
      {loading || showSpinner ? <CircularProgress size={15} /> : <Iconify icon="solar:menu-dots-bold" rotate={45} />}
    </IconButton>
    {open && <Menu
      id="basic-menu"
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      MenuListProps={{
        'aria-labelledby': 'basic-button',
      }}
    >
      <MenuItem onClick={(e) => {setShareLink(undefined); setShareFor("1"); setDialogOpen(true); e.preventDefault(); e.stopPropagation(); handleClose();}}>Share Location</MenuItem>
      <MenuItem onClick={(e) => {setDeviceForHistory(device); e.preventDefault(); e.stopPropagation(); handleClose();}}>Device History</MenuItem>

      {device.externalIdentifier.startsWith("5|") && <MenuItem onClick={(e) => {
        setLoading(true);
        axios.post(endpoints.eldEngineImmobilizer.requestUpdate, {
          deviceId: device.id,
        }).then((response) => {
          // here we need to start listening to changes to this device until we receive it
          onEldEngineImmobilizerUpdated(response.data.eldEngineImmobilizer as any)

          async function keepLoadingData(id: number, executedTimes: number) {
            // at most we'll execute this 18 times
            if (executedTimes >= 18) {
              return;
            }
            let done = false;
            try {
              const eldEngineImmobilizers: EldEngineImmobilizer[] = await axios.get(endpoints.eldEngineImmobilizer.findAll).then(r => r.data);

              // if status and location have been loaded then we're done reloading the data each 10sec
              const eldEngineImmobilizerFound = eldEngineImmobilizers.find(l => l.id === id);

              if (eldEngineImmobilizerFound) {
                updateData("eldEngineImmobilizers")(eldEngineImmobilizers)
                if (eldEngineImmobilizerFound.whereLoadedAt) {
                  if (eldEngineImmobilizerFound.whereLoadedAt != null && dayjs(eldEngineImmobilizerFound.whereLoadedAt).isAfter(dayjs(eldEngineImmobilizerFound.whereRequestedAt))) {
                    if (eldEngineImmobilizerFound.statusLoadedAt != null && dayjs(eldEngineImmobilizerFound.statusLoadedAt).isAfter(dayjs(eldEngineImmobilizerFound.whereRequestedAt))) {
                      done = true;
                      axios.get(endpoints.devices.findAll).then(processDevicesUpdateResponse);
                    }
                  }
                }
              }
            } finally {
              if (!done) {
                setTimeout(() => {
                  keepLoadingData(id, executedTimes+1);
                }, 10000);
              }
            }
          }

          keepLoadingData(response.data.eldEngineImmobilizer.id, 0);

          enqueueSnackbar("Successfully sent a location request to device. Please wait 30-45sec to get the latest location.");
        }).catch((error) => {
          if (error?.response?.status === 400) {
            enqueueSnackbar("There was an error processing the request.", {variant: "error"});
          } else {
            enqueueSnackbar("An unexpected error has occured. Please check your internet connection and try again.", { variant: "error" });
          }
        }).finally(() => {
          setLoading(false);
        })
        e.preventDefault();
        e.stopPropagation();
        handleClose();
        // updateData("eldEngineImmobilizers")(replaceById(data!.accidents!, accident));
      }}>Load Status & Location</MenuItem>}
      {device.externalIdentifier.startsWith("5|") && <MenuItem onClick={(e) => {
        if (!eldEngineImmobilizer) {
          return;
        }
        if (!eldEngineImmobilizer.statusLoadedAt || dayjs(eldEngineImmobilizer.statusLoadedAt).isBefore(dayjs().add(-3, 'minutes'))) {
          enqueueSnackbar("Please load the current Status & Location of the Vehicle", {variant: "error"});
          return;
        }
        if (eldEngineImmobilizer.speed !== 0) {
          enqueueSnackbar(`Please wait until the Vehicle is not moving to toggle it On/Off (speed is ${  eldEngineImmobilizer.speed  })`, {variant: "error"});
          return;
        }
        setLoading(true);

        axios.post(endpoints.eldEngineImmobilizer.togglePower, {
          deviceId: device.id,
          relayOn: eldEngineImmobilizer.relayOn,
        }).then(() => {
          enqueueSnackbar("Successfully initiated a Power Toggle Request. Please wait 30-45sec and request the Status and the Location again.")
        }).catch((error) => {
          if (error.response?.status === 403) {
            enqueueSnackbar("Please wait for the location to be loaded before Powering the Immobilizer on/off", { variant: "error" });
          } else {
            enqueueSnackbar("An unexpected error has occured. Please check your internet connection and try again.", { variant: "error" })
          }
        }).finally(() => {
          setLoading(false);
        })
        e.preventDefault();
        e.stopPropagation();
        handleClose();
      }}>Power {eldEngineImmobilizer?.relayOn ? 'On' : 'Off'}</MenuItem>}
    </Menu>}

    {dialogOpen && <SimpleDialog
      title="Share Location"
      open={dialogOpen}
      onCancel={() => {
        setDialogOpen(false);
      }}
      onSubmit={shareLink ? undefined : () => {
        setLoading(true);

        axios.post(endpoints.devices.createShareLink, {
          deviceId: device.id,
          durationDays: Number(shareFor),
        }).then((response) => {
          console.log(response);
          setShareLink(response.data.shareLink);
        }).finally(() => {
          setLoading(false);
        });
      }}
      submitDisabled={loading}
      submitButton="Share Location"
      submitButtonIcon={<Iconify icon="iconamoon:location-duotone" />}
      submitButtonColor="info">

      <Stack direction="column" gap={1}>
        {shareLink
          ? <TruxFormField label="Share Link">
              <Input
              sx={{width: "100%"}}
                type="text"
                disabled
                value={window.location.origin + shareLink}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(window.location.origin + shareLink);
                          enqueueSnackbar("Copied the Share Link");
                        } catch (err) {
                          enqueueSnackbar("Share Link could not be coppied", { variant: 'warning' });
                        }
                      }}
                    >
                      <Iconify icon="solar:copy-linear" />
                    </IconButton>
                  </InputAdornment>
                }
              />
            </TruxFormField>
          : <TruxFormField label="Share For">
            <TruxSimpleSelectField value={shareFor} onChange={(val) => {
              setShareFor(val);
            }}>
              <MenuItem value="1">1 Day</MenuItem>
              <MenuItem value="7">7 Days</MenuItem>
              <MenuItem value="31">31 Days</MenuItem>
            </TruxSimpleSelectField>
          </TruxFormField>
        }
      </Stack>
    </SimpleDialog>}
  </>
}

function escapeHTML(str: string) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function getVelocityColor(device: DeviceWithMapInfo) {
  if (overSpeed(device)) {
    return "#ff5631";
  }
  return " #4CAF50";
}

function overSpeed(device: DeviceWithMapInfo) {
  if (device.velocity === undefined) {
    return false;
  }

  let speedLimit = 65;
  if (device.location && device.location.length >= 2) {
    const state = device.location.substring(device.location.length - 2);
    if ((speedPerState as any)[state]) {
      speedLimit = (speedPerState as any)[state];
    }
  }

  return speedLimit + 1 < device.velocity;
}

function getPopup(device: DeviceWithMapInfo, timezone: string, eldConnectedVehicleUnitNumbers: string[]) {
  // ${// eslint-disable-next-line no-nested-ternary
  //   device.deviceType.startsWith("ELD Truck") ? (eldConnectedVehicleUnitNumbers?.includes(device.deviceType.replace("ELD Truck #", "")) ? `<div style="border: 1px solid rgba(49, 187, 63, 0.7); color: #31bb3f; padding: 2px; padding-left: 3px; padding-right: 3px; border-radius: 6px">ELD Connected</div>` : `<div style="border: 1px solid rgba(245, 82, 60, 0.7); color: #F5523C; padding: 2px; padding-left: 3px; padding-right: 3px; border-radius: 6px">ELD Disconnected</div>`): ""}
  // return `<div style="display:flex; gap: 2px; color: #444; flex-direction:column; align-items:center">
  //   <a href="https://maps.google.com/?q=${device.lastPosLat},${device.lastPosLong}">${device.deviceType}</a>
  //   <div>${device.lastPosLat}, ${device.lastPosLong}</div>
  //   <div>${formatDatetimeForPreview(device.lastPing, timezone)}</div>
  //   ${device.externalIdentifier ? `<div>Battery: ${device.battery}%</div>`: ""}
  //   ${!!device.velocity && device.velocity > 0 ? `<div>Speed: ${device.velocity.toFixed(2)}mph</div>`: ""}

  // </div>`
  return `
  <div style="display: flex; align-items: center; margin-bottom: 0px;">
      <div style="display: flex; justify-content: center; margin-right: 10px; width: 30px; text-align: center;">
          ${device.deviceType.startsWith("ELD Truck #")
            ? `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="24" viewBox="0 0 36 36"><path fill="currentColor" d="M30 12h-4V7a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v18a1 1 0 0 0 1 1h1V8h20v11.7a6.5 6.5 0 0 1 1.56-.2H26V14h4a2 2 0 0 1 2 2v1h-4v2h4v5h-2.4a4.54 4.54 0 0 0-8.34 0h-6.83a4.5 4.5 0 0 0-4.17-2.76A4.38 4.38 0 1 0 14.72 26H21a4.49 4.49 0 0 0 8.92 0H33a1 1 0 0 0 1-1v-9a4 4 0 0 0-4-4M10.26 28a2.38 2.38 0 1 1 0-4.75a2.38 2.38 0 1 1 0 4.75m15.17 0a2.38 2.38 0 1 1 2.5-2.37a2.44 2.44 0 0 1-2.5 2.37" class="clr-i-outline clr-i-outline-path-1"/><path fill="none" d="M0 0h36v36H0z"/></svg>`
            : `<svg xmlns="http://www.w3.org/2000/svg" width="28" style="margin-left: 2px" height="24" viewBox="0 0 36 36"><path fill="currentColor" d="M15 19.2c-3.2 0-5.8 2.6-5.8 5.8s2.6 5.8 5.8 5.8s5.8-2.6 5.8-5.8s-2.6-5.8-5.8-5.8m0 10c-2.3 0-4.2-1.9-4.2-4.2s1.9-4.2 4.2-4.2s4.2 1.9 4.2 4.2s-1.9 4.2-4.2 4.2" class="clr-i-outline clr-i-outline-path-1"/><path fill="currentColor" d="M14 24h2v2h-2z" class="clr-i-outline clr-i-outline-path-2"/><path fill="currentColor" d="M33 9H2v13.1C2 24.3 3.7 26 5.9 26H7v-2H5.9c-1 0-1.8-.8-1.9-1.9V15h22v7.1c0 1-.8 1.8-1.9 1.9H23v2h1.1c2.1 0 3.8-1.7 3.8-3.9V11h5c.6 0 1-.4 1-1s-.3-1-.9-1m-7 4H4v-2h22z" class="clr-i-outline clr-i-outline-path-3"/><path fill="none" d="M0 0h36v36H0z"/></svg>`
          }
      </div>
      <div style="font-size: 16px; font-weight: bold;">${device.deviceType}</div>
  </div>

  ${device.eldDrivers?.map((eldDriver) => `<div style="display: flex; align-items: center;">
      <div style="display: flex; justify-content: center; margin-right: 10px; width: 30px; text-align: center;">
        <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 24 24"><g fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="6" r="4"/><path d="M20 17.5c0 2.485 0 4.5-8 4.5s-8-2.015-8-4.5S7.582 13 12 13s8 2.015 8 4.5Z"/></g></svg>
      </div>
      <div style="font-size: 14px;">
        ${escapeHTML(eldDriver.firstName)} ${escapeHTML(eldDriver.lastName)}
      </div>
        <div style="margin-left: 5px">${eldDriver.connected
        ? `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 256 256"><path fill="#4CAF50" d="M168 72v128a8 8 0 0 1-16 0V72a8 8 0 0 1 16 0m32-48a8 8 0 0 0-8 8v168a8 8 0 0 0 16 0V32a8 8 0 0 0-8-8m-80 80a8 8 0 0 0-8 8v88a8 8 0 0 0 16 0v-88a8 8 0 0 0-8-8m-40 40a8 8 0 0 0-8 8v48a8 8 0 0 0 16 0v-48a8 8 0 0 0-8-8m-40 40a8 8 0 0 0-8 8v8a8 8 0 0 0 16 0v-8a8 8 0 0 0-8-8"/></svg>`
        : `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 256 256"><path fill="#ff5631" d="M88 152v48a8 8 0 0 1-16 0v-48a8 8 0 0 1 16 0m-48 32a8 8 0 0 0-8 8v8a8 8 0 0 0 16 0v-8a8 8 0 0 0-8-8m173.92 26.62l-160-176a8 8 0 1 0-11.84 10.76L112 122.29V200a8 8 0 0 0 16 0v-60.11l24 26.4V200a8 8 0 0 0 16 0v-16.11l34.08 37.49a8 8 0 1 0 11.84-10.76m-53.92-87a8 8 0 0 0 8-8V72a8 8 0 0 0-16 0v43.63a8 8 0 0 0 8 8Zm40 44a8 8 0 0 0 8-8V32a8 8 0 0 0-16 0v127.63a8 8 0 0 0 8 8Z"/></svg>`}</div>
  </div>`)?.join("") || ""}

  <div style="display: flex; align-items: center; margin-bottom: 7px;"></div>

  <hr style="border: none; height: 1px; background-color: #e0e0e0; margin: 7px 0;">

  <div style="display: flex; align-items: center; margin-bottom: 7px;">
      <div style="display: flex; justify-content: center; margin-right: 10px; width: 30px; text-align: center;">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><g fill="none" stroke="#777" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"><path d="M12.56 20.82a.96.96 0 0 1-1.12 0C6.611 17.378 1.486 10.298 6.667 5.182A7.6 7.6 0 0 1 12 3c2 0 3.919.785 5.333 2.181c5.181 5.116.056 12.196-4.773 15.64"/><path d="M12 12a2 2 0 1 0 0-4a2 2 0 0 0 0 4"/></g></svg>
      </div>
      <div style="font-size: 14px; color: #777;">
        ${device.location ? `${device.location}<br />` : ""}
        <a href="https://maps.google.com/?q=${device.lastPosLat},${device.lastPosLong}">${device.lastPosLat}, ${device.lastPosLong}</a>
      </div>
  </div>

  <hr style="border: none; height: 1px; background-color: #e0e0e0; margin: 7px 0;">

  <div style="display: flex; align-items: center;">
    <div style="display: flex; justify-content: center; margin-right: 10px; width: 30px; text-align: center;">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
        <g fill="none" stroke="#777" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></g></svg>
    </div>
    <div style="font-size: 14px; color: #777;">Last Ping: ${formatDatetimeForPreview(device.lastPing, timezone)}</div>
  </div>

  ${device.externalIdentifier && !device.externalIdentifier.startsWith("2|") ? `<div style="display: flex; align-items: center; margin-top: 4px">
      <div style="display: flex; justify-content: center; margin-right: 10px; width: 30px; text-align: center;">
          <svg viewBox="0 0 24 24" width="24" height="24" fill="#4CAF50">
              <path d="M17 5.33C17 4.6 16.4 4 15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33V20.67C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33zM13 18h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V8h2v2z"/>
          </svg>
      </div>
      <div style="font-size: 16px; font-weight: bold; color: #4CAF50;">${device.battery}%</div>
  </div>` : ""}

  ${!!device.velocity && device.velocity > 0 ? `<div style="display: flex; align-items: center; margin-top: 4px">
      <div style="display: flex; justify-content: center; margin-right: 10px; width: 30px; text-align: center;">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <path fill="${getVelocityColor(device)}" d="M10.45 15.5q.6.6 1.55.588t1.4-.688L19 7l-8.4 5.6q-.675.45-.712 1.375t.562 1.525M12 4q1.475 0 2.838.412T17.4 5.65l-1.9 1.2q-.825-.425-1.712-.637T12 6Q8.675 6 6.337 8.338T4 14q0 1.05.288 2.075T5.1 18h13.8q.575-.95.838-1.975T20 13.9q0-.9-.213-1.75t-.637-1.65l1.2-1.9q.75 1.175 1.188 2.5T22 13.85t-.325 2.725t-1.025 2.475q-.275.45-.75.7t-1 .25H5.1q-.525 0-1-.25t-.75-.7q-.65-1.125-1-2.387T2 14q0-2.075.788-3.887t2.15-3.175t3.187-2.15T12 4m.175 7.825"/>
          </svg>
      </div>
      <div style="font-size: 16px; font-weight: bold; color:${getVelocityColor(device)}">${device.velocity.toFixed(2)} mph</div>
  </div>` : ""}

  <div style="display: flex; align-items: center; margin-bottom: 7px;" />

`;
  // ${device.externalIdentifier ? `<div>Battery: ${device.battery}%</div>`: ""}
  //       ${!!device.velocity && device.velocity > 0 ? `<div>Speed: ${device.velocity.toFixed(2)}mph</div>`: ""}

//   <div style="display: flex; align-items: center; margin-bottom: 7px;">
//       <div style="margin-right: 15px; width: 30px; text-align: center;">
//           <svg viewBox="0 0 24 24" width="24" height="24" fill="#333">
//               <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
//           </svg>
//       </div>
//       <div style="font-size: 14px; font-weight: bold;">DD Freight Express Inc.</div>
//   </div>
//   <div style="display: flex; align-items: center;">
//   <div style="margin-right: 15px; width: 30px; text-align: center;">
//       <svg viewBox="0 0 24 24" width="24" height="24" fill="#FF9800">
//           <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z"/>
//       </svg>
//   </div>
//   <div style="font-size: 16px; font-weight: bold; color: #FF9800;">2 Vehicle Faults</div>
// </div>
}

function DeviceMap({
  data,
  filteredDevices,
  selectedUuid,
  mapData,
  setMapData,
  deviceHistory,
  setSelectedMarker,
  timezone,
  processDevicesUpdateResponse,
}: {
  data: PageModel,
  filteredDevices: DeviceWithMapInfo[],
  selectedUuid: string,
  mapData: MapData | null,
  setMapData: React.Dispatch<React.SetStateAction<MapData | null>>
  deviceHistory: DeviceHistoryLineWithCityName[] | null;
  setSelectedMarker: (index: number) => void,
  timezone: string,
  processDevicesUpdateResponse: (response: any) => void
}) {
  const [node, setNode] = useState(null);
  const layerGroupRef = useRef<L.LayerGroup<any> | null>(null);
  const weighStationGroup = useRef<L.LayerGroup<any> | null>(null);
  const markerGroup = useRef<L.MarkerClusterGroup | null>(null);
  const historyLayerGroupRef = useRef<L.LayerGroup<any> | null>(null);

  const mapTypeSelectorState = useMapTypeSelector();

  useEffect(() => {
    const map = (node as any)?.map as L.Map | undefined;
    if (map) {
      map.eachLayer((layer) => {
        if (layer instanceof L.TileLayer) {
          map.removeLayer(layer);
        }
      });
      const currentZoom = map.getZoom();
      const currentCenter = map.getCenter();

      if (mapTypeSelectorState.defaultSelected) {
        L.tileLayer.provider('Stadia.AlidadeSmooth').addTo(map)
      } else {
        L.tileLayer('https://tiles.stadiamaps.com/tiles/alidade_satellite/{z}/{x}/{y}{r}.jpg', {
          minZoom: 0,
          maxZoom: 20,
        }).addTo(map);
      }
      map.setView(currentCenter, currentZoom);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(node as any)?.map, mapTypeSelectorState.defaultSelected]);


  useEffect(() => {
    if (node && !(node as any).map) {
      console.log("INITIALIING MAP");
      const _map = L.map('map', { attributionControl: false }).setView([40, -96], 5);

      if (mapTypeSelectorState.defaultSelected) {
        L.tileLayer.provider('Stadia.AlidadeSmooth').addTo(_map)
      } else {
        L.tileLayer('https://tiles.stadiamaps.com/tiles/alidade_satellite/{z}/{x}/{y}{r}.jpg', {
          minZoom: 0,
          maxZoom: 20,
        }).addTo(_map);
      }


      layerGroupRef.current = L.layerGroup().addTo(_map);
      weighStationGroup.current = L.layerGroup().addTo(_map);
      markerGroup.current = L.markerClusterGroup({showCoverageOnHover: false, disableClusteringAtZoom: 10, spiderfyOnMaxZoom: false}).addTo(_map);
      historyLayerGroupRef.current = L.layerGroup().addTo(_map);

      setMapData({ map: _map, markersData: [] });
      (node as any).map = _map;

      return () => {
        if (_map && _map.remove) {
          _map.off();
          _map.remove();
        }
      }
    }
    return () => { }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node, setMapData]);

  useEffect(() => {
    // console.log("REDOING MAP");
    if (mapData) {
      const squareConnected = L.divIcon({
        html: `
          <svg xmlns="http://www.w3.org/2000/svg" width="14px" height="14px" viewBox="0 0 56 56">
            <rect x="3" y="3" width="50" height="50" rx="6" fill="#36b746" stroke="white" stroke-width="3"/>
          </svg>
        `,
        className: '',
        iconSize: [14, 14],
      });

      const squareDisconnected = L.divIcon({
        html: `
          <svg xmlns="http://www.w3.org/2000/svg" width="14px" height="14px" viewBox="0 0 56 56">
            <rect x="3" y="3" width="50" height="50" rx="6" fill="#939393" stroke="white" stroke-width="3"/>
          </svg>
        `,
        className: '',
        iconSize: [14, 14],
      });;

      const getArrowConnected = (direction: number) => L.divIcon({
        html: `
          <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"> <!-- Drop shadow filter --> <defs> <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%"> <feDropShadow dx="0" dy="1" stdDeviation="2" flood-opacity="0.2"/> </filter> </defs> <!-- Arrow shape - more streamlined --> <path d="M50,10 L85,75 L50,55 L15,75 Z" fill="#4CAF50" stroke="#FFFFFF" stroke-width="4" stroke-linejoin="round" filter="url(#shadow)" transform="rotate(${direction || 0} 50 50)"/> </svg>
        `,
        className: '',
        iconSize: [23, 23],
      });

      const getArrowDisconnected = (direction: number) => L.divIcon({
        html: `
          <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"> <!-- Drop shadow filter --> <defs> <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%"> <feDropShadow dx="0" dy="1" stdDeviation="2" flood-opacity="0.2"/> </filter> </defs> <!-- Arrow shape - more streamlined --> <path d="M50,10 L85,75 L50,55 L15,75 Z" fill="#939393" stroke="#333333" stroke-width="4" stroke-linejoin="round" filter="url(#shadow)" transform="rotate(${direction || 0} 50 50)"/> </svg>
        `,
        className: '',
        iconSize: [20, 20],
      });

      const openedUuids: string[] = [];
      mapData.markersData?.forEach(markerData => {
        if (markerData.marker.isPopupOpen()) {
          openedUuids.push(markerData.deviceUuid);
        }
      })

      markerGroup.current?.clearLayers();
      historyLayerGroupRef.current?.clearLayers();

      if (deviceHistory && deviceHistory.length > 0) {
        const historyPoints: L.LatLng[] = [];

        deviceHistory.forEach((line, index) => {
          if (!line.posLat || !line.posLong) return;
          const lat = Number(line.posLat);
          const lon = Number(line.posLong);
          const marker = L.marker([lat, lon], {
            icon: L.divIcon({
              html: `
                <svg xmlns="http://www.w3.org/2000/svg" width="8px" height="8px" viewBox="0 0 5 5">
                  <circle cx="2.5" cy="2.5" r="2.5" fill="#1E90FF" stroke="#fff" stroke-width="0.3"/>
                </svg>
              `,
              className: '',
              iconSize: [14, 14],
              iconAnchor: [7, 7],
            })
          });
          marker.on('click', () => {
            setSelectedMarker(index);
            const timer = setTimeout(() => {
              setSelectedMarker(-1);
            }, 2000);
            return () => clearTimeout(timer);
          });
          marker.addTo(historyLayerGroupRef.current!);
          historyPoints.push(L.latLng(lat, lon))
        });
        const bounds = L.latLngBounds(historyPoints);
        if (bounds.isValid()) {
          mapData.map.fitBounds(bounds);
        }
      } else if (filteredDevices.length) {
          filteredDevices.forEach(device => {
            if (device.lastPosLat !== "" && device.lastPosLong !== "") {

              let marker = null as L.Marker | null;
              mapData.markersData.forEach(markerData => {
                if (markerData.deviceUuid === device.deviceUuid) {
                  marker = markerData.marker;
                }
              })
              if (!device.lastPing) {
                return;
              }

              const isConnected = !device.lastPing || dayjs(device.lastPing).isAfter(dayjs().add(-10, 'minutes'));
              if (marker) {
                marker.setLatLng([Number(device.lastPosLat), Number(device.lastPosLong)]);
                // eslint-disable-next-line no-nested-ternary
                marker.setIcon(device.direction !== null && device.direction !== undefined && device.direction > -1 && device.direction !== 0
                  ? (isConnected ? getArrowConnected(device.direction) : getArrowDisconnected(device.direction))
                  : (isConnected ? squareConnected : squareDisconnected));

                if (markerGroup.current) {
                  marker.addTo(markerGroup.current);
                }

                const popup = marker.getPopup();
                if (popup) {
                  popup.setContent(getPopup(device, timezone, data.eldConnectedVehicleUnitNumbers));
                  if (openedUuids.includes(device.deviceUuid)) {
                    marker.openPopup();
                  }
                }

              } else {
                marker = new L.Marker(
                  [Number(device.lastPosLat), Number(device.lastPosLong)],
                  { icon:
                    // eslint-disable-next-line no-nested-ternary
                    device.direction !== null && device.direction !== undefined && device.direction > -1 && device.direction !== 0
                      ? (isConnected ? getArrowConnected(device.direction) : getArrowDisconnected(device.direction))
                      : (isConnected ? squareConnected : squareDisconnected)
                  }
                );

                const popup = L.popup({
                  closeButton: false,
                  content: getPopup(device, timezone, data.eldConnectedVehicleUnitNumbers),
                });
                marker.bindPopup(popup);

                if (markerGroup.current) {
                  marker.addTo(markerGroup.current);
                }

                const markerFinal = marker;
                setMapData(currentMapData => {
                  currentMapData?.markersData.push({ marker: markerFinal, deviceUuid: device.deviceUuid });
                  return currentMapData;
                })
              }
            }
          })
        }

      markerGroup.current?.refreshClusters();
      // historyLayerGroupRef.current?.refreshClusters();
    }
  }, [filteredDevices, deviceHistory, mapData, setMapData, setSelectedMarker, timezone, data.eldConnectedVehicleUnitNumbers])

  useEffect(() => {
    if (mapData) {
      mapData.markersData.forEach(markerData => {
        if (markerData.deviceUuid === selectedUuid) {
          markerData.marker.openPopup()

          mapData?.map?.panTo(markerData?.marker?.getLatLng());
        } else {
          markerData.marker.closePopup()
        }
      })
    }
  }, [selectedUuid, mapData])

  const loadingRef = useRef(false);

  const [selectedOptions, setSelectedOptions] = useState([] as string[]);

  return <Box sx={{ flexGrow: 1, position: "relative" }}>
    <Box sx={{
      position: "absolute",
      top: "20px",
      right: "20px",
      zIndex: "1000",
      background: "white",
      borderRadius: "4px",
      boxShadow: "0 0 10px rgba(0,0,0,0.2)",
      display:"flex",
      flexDirection:"row",
    }}>
      <Button variant="outlined" sx={{fontWeight: "300"}} onClick={() => {
        if (loadingRef.current) {
          return;
        }
        loadingRef.current = true;
        axios.get(endpoints.devices.findAll).then(processDevicesUpdateResponse).finally(() => {
          loadingRef.current = false;
        });
      }}><Iconify icon="solar:refresh-outline" /> <Box sx={{pl: "5px"}}>Refresh</Box></Button>

    </Box>

    <MapTypeSelector mapTypeSelectorState={mapTypeSelectorState} />

    <MapOptionSelector selectedOptions={selectedOptions} setSelectedOptions={(options) => {
      console.log(options);
      setSelectedOptions(options);

      if (mapData?.map) {
        if (options.includes("Landmarks")) {
          const savedShapes = [] as Shape[];

          data.eldLandmarks?.forEach(eldLandmark => {
            const {polygons, circles} = eldLandmark;

            polygons.forEach(polygon => {
              savedShapes.push({
                type: "Polygon",
                coordinates: polygon
              })
            });

            circles.forEach(circle => {
              savedShapes.push({
                type: "Circle",
                center: new L.LatLng(circle.center[0], circle.center[1]),
                radius: circle.radius,
              })
            })
          })

          layerGroupRef.current?.clearLayers();

          // Add each shape to the map
          savedShapes.forEach(shape => {
            console.log("ADDING SAVED SHAPES")
            if (shape.type === 'Circle') {
              L.circle(shape.center, {
                radius: shape.radius
              }).addTo(layerGroupRef.current!);
            } else if (shape.type === 'Polygon') {
              L.polygon(shape.coordinates).addTo(layerGroupRef.current!);
            }
          });
        } else {
          layerGroupRef.current?.clearLayers();
        }
        if (options.includes("Weigh Station")) {
          weighStationGroup.current?.clearLayers();
          getStationData().then(d => {
            d.forEach(l => {
              // L.marker([32, 80]).addTo(weighStationGroup.current!);

              L.marker([Number(l.lat), Number(l.lon)], {icon: L.divIcon({
                html: `
                  <svg xmlns="http://www.w3.org/2000/svg" width="5px" height="5px" viewBox="0 0 5 5">
                    <circle cx="2.5" cy="2.5" r="2.5" fill="#2196F3"/>
                  </svg>
                `,
                className: '',
                iconSize: [5, 5],
              })})
              .addTo(weighStationGroup.current!)
              .bindPopup(l.stationId, {className: "popup-no-tip", closeButton: false });
            });
          })

        } else {
          weighStationGroup.current?.clearLayers();
        }
      }
    }} />

    <div className="parent-container" ref={setNode as any}>
      <div className="child-div" id="map" />
    </div>
  </Box>
}

async function getStationData() {
  try {
      // Download the CSV file
      const response = await fetch('https://accountingimageuploads.s3.us-west-2.amazonaws.com/ws.json');
      const text = await response.text();

      const stationData = [] as {stationId: string, lat: string, lon: string}[];
      const lines = JSON.parse(text);
      for (let i = 0; i < lines.length; i+=1) {
        const line = lines[i];

        stationData.push({stationId: `${line.address}, ${line.city}, ${line.state} ${line.zip}`, lat: line.lat, lon: line.lon});
      }

      return stationData;
  } catch (error) {
      console.error('Error:', error.message);
      throw error;
  }
}


function MapOptionSelector({selectedOptions, setSelectedOptions}: {selectedOptions: string[], setSelectedOptions: (a: string[]) => void}) {
  const [contextMenu, setContextMenu] = useState<{mouseX: number;mouseY: number;} | null>(null);
  const options = ["Landmarks", "Weigh Station"];
  return <Box sx={{
    position: "absolute",
    bottom: "20px",
    left: "20px",
    zIndex: "1000",
    background: "white",
    borderRadius: "4px",
    boxShadow: "0 0 10px rgba(0,0,0,0.2)",
    display:"flex",
    flexDirection:"row",
    gap: "5px"
  }}>
    <Button variant="outlined" sx={{fontWeight: "300"}} onClick={(event) => {
      setContextMenu(
        contextMenu === null
          ? {
              mouseX: event.clientX + 2,
              mouseY: event.clientY - 6,
            }
          : null,
      );
    }}>Options ({selectedOptions.length})</Button>
    <Menu
      open={contextMenu !== null}
      onClose={() => setContextMenu(null)}
      anchorReference="anchorPosition"
      anchorPosition={
        contextMenu !== null
          ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
          : undefined
      }
    >
      {options.map((v, i) => <MenuItem key={i} sx={{background: selectedOptions.includes(v) ? "#DDD" : undefined}} onClick={() => {
        if (selectedOptions.includes(v)) {
          selectedOptions.splice(selectedOptions.indexOf(v), 1);
        } else {
          selectedOptions.push(v);
        }
        setSelectedOptions([...selectedOptions]);
      }}>{v}</MenuItem>)}
    </Menu>
  </Box>
}